import React, { useEffect, useState, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import './components/styles/global.css';
// Статически: точки входа (первый экран), защита маршрутов
import Home from "./components/Home";
import Registration from "./components/Registration";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { useDispatch } from "react-redux";
import { auth } from "./action/user";
import { useSelector } from 'react-redux';


import './i18n'; // Подключение i18n для инициализации
import { useTranslation } from 'react-i18next';
import { init as initTelegram, useTelegramBackButton, isTelegramWebApp } from "./utils/telegramWebApp";
import TelegramNotLinked from "./components/TelegramNotLinked";
import { tt } from './i18n';

// Global UI helpers
import Toast from './components/Toast';
import Confirm from './components/Confirm';

// Защита от ChunkLoadError после деплоя: старая вкладка запрашивает чанк,
// которого уже нет (сервер отдаёт index.html). Перезагружаем страницу один раз.
const CHUNK_RELOAD_FLAG = 'chunk-reloaded';

const isChunkLoadError = (error) =>
  !!error && (error.name === 'ChunkLoadError' ||
    /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(String(error.message)));

function lazyWithReload(factory) {
  return React.lazy(() => factory()
    .then((module) => {
      // Загрузка прошла успешно: сбрасываем флаг
      try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch (e) { /* sessionStorage недоступен */ }
      return module;
    })
    .catch((error) => {
      // Если флаг не удалось прочитать, считаем перезагрузку уже была (защита от цикла)
      let alreadyReloaded = true;
      try { alreadyReloaded = !!sessionStorage.getItem(CHUNK_RELOAD_FLAG); } catch (e) { /* sessionStorage недоступен */ }

      if (isChunkLoadError(error) && !alreadyReloaded) {
        try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1'); } catch (e) { /* sessionStorage недоступен */ }
        window.location.reload();
        // Никогда не завершаемся: React ничего не рендерит до перезагрузки
        return new Promise(() => {});
      }
      throw error;
    }));
}

// Страницы маршрутов: отдельные чанки, загружаются по требованию
const Privacy = lazyWithReload(() => import("./components/pages/Privacy"));
const Refund = lazyWithReload(() => import("./components/pages/Refund"));
const Prohibited = lazyWithReload(() => import("./components/pages/Prohibited"));
const PublicContacts = lazyWithReload(() => import("./components/pages/PublicContacts"));
const PublicTerms = lazyWithReload(() => import("./components/pages/PublicTerms"));
const Main = lazyWithReload(() => import("./components/Main"));
const Parcels = lazyWithReload(() => import("./components/Parcels"));
const Notification = lazyWithReload(() => import("./components/Notification"));
const Profile = lazyWithReload(() => import("./components/Profile"));
const LostClient = lazyWithReload(() => import("./components/LostClient"));
const ScanLab = lazyWithReload(() => import("./components/dashboard/ScanLab"));
const Dashboard = lazyWithReload(() => import("./components/dashboard/Dashboard"));
const ArchivePage = lazyWithReload(() => import("./components/ArchivePage"));
const NotFound = lazyWithReload(() => import("./components/NotFound"));
const AnnouncementManager = lazyWithReload(() => import("./components/AnnouncementManager"));
// Pages
const Bonuses = lazyWithReload(() => import('./components/pages/Bonuses'));
const Filials = lazyWithReload(() => import('./components/pages/Filials'));
const Invoices = lazyWithReload(() => import('./components/pages/Invoices'));
const Warehouse = lazyWithReload(() => import('./components/pages/Warehouse'));
const QRPage = lazyWithReload(() => import('./components/pages/QRPage'));
const Terms = lazyWithReload(() => import('./components/pages/Terms'));
const Contacts = lazyWithReload(() => import('./components/pages/Contacts'));
const Settings = lazyWithReload(() => import('./components/pages/Settings'));
const Manager = lazyWithReload(() => import('./components/pages/Manager'));
const ForgotPassword = lazyWithReload(() => import('./components/pages/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('./components/pages/ResetPassword'));
const PublicOffer = lazyWithReload(() => import('./components/pages/PublicOffer'));
const Calculator = lazyWithReload(() => import('./components/pages/Calculator'));

function TelegramBack() {
  useTelegramBackButton();
  return null;
}

function App() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [tgNotLinked, setTgNotLinked] = useState(null);

  useEffect(() => {
    // Проверяем авторизацию при загрузке и ждём результата
    const checkAuth = async () => {
      initTelegram();
      const res = await dispatch(auth());
      if (res && res.notLinked) setTgNotLinked(res);
      setIsLoading(false);
    };
    checkAuth();
  }, [dispatch]);

  const isAuth = useSelector(state => state.user.isAuth);
  const { i18n } = useTranslation(); // при смене языка перерисовываем всё приложение (key ниже)

  // Во время загрузки показываем заглушку
  if (isLoading) {
    return <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
      <p>{tt('Загрузка...')}</p>
    </div>;
  }

  if (tgNotLinked && !isAuth) {
    return (
      <TelegramNotLinked
        botUsername={tgNotLinked.botUsername}
        onLogin={() => { window.history.replaceState(null, '', '/login'); setTgNotLinked(null); }}
      />
    );
  }

  return (
    <Router key={i18n.language}>
      <TelegramBack />
      <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}>{tt('Загрузка...')}</div>}>
      <Routes>
        {/* Публичные маршруты */}
        {/* В мини-приложении Telegram вошедший пользователь сразу попадает в кабинет, а не на лендинг */}
        <Route path="/" element={isAuth && isTelegramWebApp() ? <Navigate to="/main" replace /> : <Home />} />
        <Route path="/public-offer" element={<PublicOffer />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/prohibited" element={<Prohibited />} />
        <Route path="/login" element={isAuth ? <Navigate to="/main" /> : <Login />} />
        <Route path="/forgot-password" element={isAuth ? <Navigate to="/main" /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/registration" element={isAuth ? <Navigate to="/main" /> : <Registration />} />
        
        {/* Защищенные маршруты (доступны только авторизованным пользователям) */}
        <Route path="/main" element={<ProtectedRoute><Main /></ProtectedRoute>} />
        <Route path="/parcels" element={<ProtectedRoute><Parcels /></ProtectedRoute>} />
        <Route path="/notification" element={<ProtectedRoute><Notification /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/account" element={<Navigate to="/profile" replace />} />
        <Route path="/scan-lab" element={<ProtectedRoute><ScanLab /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/lost" element={<ProtectedRoute><LostClient /></ProtectedRoute>} />
        <Route path="/archive" element={<ProtectedRoute><ArchivePage /></ProtectedRoute>} />

        {/* Pages for main menu (защищенные) */}
        <Route path="/bonus" element={<ProtectedRoute><Bonuses /></ProtectedRoute>} />
        <Route path="/filials" element={<ProtectedRoute><Filials /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/warehouse" element={<ProtectedRoute><Warehouse /></ProtectedRoute>} />
        <Route path="/qr" element={<ProtectedRoute><QRPage /></ProtectedRoute>} />
        <Route path="/terms" element={isAuth ? <ProtectedRoute><Terms /></ProtectedRoute> : <PublicTerms />} />
        <Route path="/contacts" element={isAuth ? <ProtectedRoute><Contacts /></ProtectedRoute> : <PublicContacts />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/manager" element={<ProtectedRoute><Manager /></ProtectedRoute>} />
        <Route path="/calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
        <Route path="/admin/announcements" element={<ProtectedRoute><AnnouncementManager /></ProtectedRoute>} />
        
        {/* Обработка всех остальных маршрутов */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {/* Global overlays */}
      <Toast />
      <Confirm />
    </Router>
  );
}

export default App;
